import {
  DestroyRef,
  Injectable,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { APP_ENVIRONMENT } from '@qtek/core/api-core';
import { AuthStore, UserStore } from '@qtek/libs/store';
import { AnalyticsService } from './analytics.service';

/**
 * Wires app-level concerns (environment config + the NgRx login flow) into the
 * tool-agnostic {@link AnalyticsService}. Kept separate from the service so the
 * service has no dependency on the store or environment.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsBootstrapService {
  private readonly analytics = inject(AnalyticsService);
  private readonly authStore = inject(AuthStore);
  private readonly userStore = inject(UserStore);
  private readonly environment = inject(APP_ENVIRONMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackBar = inject(MatSnackBar);

  constructor() {
    this.analytics.init({
      enabled: Boolean(this.environment.mixpanelEnabled),
      token: this.environment.mixpanelToken,
      environment: this.environment.mixpanelEnv,
    });

    if (this.environment.analyticsDebugToasts) {
      this.analytics.tracked$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(event => {
          console.log('Mixpanel event: ', event);
          this.snackBar.open(`📊 ${event.name}`, 'Dismiss', {
            duration: 3000,
          });
        });
    }

    effect(() => {
      const person = this.userStore.person();
      const company = this.userStore.company();
      const userRelation = this.userStore.user();
      if (!person?.personId) return;

      untracked(() => {
        this.analytics.identifyUser({
          id: userRelation?._id,
          name:
            `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() ||
            person.name,
          email: userRelation?.email,
          prsId: person.personId,
          accountId: company?._id,
          accountName: company?.companyName,
        });
      });
    });

    this.authStore.loggedOut$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.analytics.reset());
  }
}
